import React, { useEffect, useState } from 'react';
import axios from 'axios';
import config from '../../../config';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, Link } from 'react-router-dom';
import {
  Search,
  Ban,
  Download,
  Eye,
  Plus,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  AlertTriangle,
  FileText,
  Loader2
} from 'lucide-react';

const OrdersList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshing, setRefreshing] = useState(false);

  const navigate = useNavigate();

  const fetchOrders = async (pageNum = page, lim = limit) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        console.error("Session expirée. Veuillez vous reconnecter.");
        navigate('/login');
        return;
      }

      const response = await axios.post(
        `${config.REACT_APP_API_URL}/getuserorderstoBreak`,
        { index: pageNum, size: lim },
        { headers: { Authorization: `Bearer ${token}`,
            'x-api-key': config.SITE_API_KEY } }
      );

      const data = response.data;
      setOrders(data.result || []);
      setTotalPages(Math.ceil((data.total || data.result?.length || 0) / lim));
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || "Erreur lors du chargement des commandes";
      setError(msg);
      console.error(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchOrders(1, limit);
    setPage(1);
  }, [limit]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchOrders(page, limit);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
    fetchOrders(newPage, limit);
  };

  const filteredOrders = orders.filter((order) => {
    const matchesSearch = order.code_orders?.toLowerCase().includes(searchTerm.toLowerCase().trim());
    const matchesStatus = filterStatus === 'all' || order.etat?.toLowerCase() === filterStatus.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  const getStatusStyle = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('terminée') || s.includes('terminé')) {
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300';
    }
    if (s.includes('en cours')) {
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300';
    }
    if (s.includes('en attente')) {
      return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
    }
    return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  };

  const handleDownload = async (filePath, fileName = 'bon-de-commande.pdf') => {
    if (!filePath || filePath === '/file/boncommande/') {
      toast.warn("Aucun fichier disponible");
      return;
    }

    try {
      const fullUrl = `${config.REACT_APP_API_URL}${filePath}`;
      const response = await axios.get(fullUrl, {
        responseType: 'blob',
        headers: { 'x-api-key': config.SITE_API_KEY }
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Téléchargement démarré");
    } catch (err) {
      console.error(err);
      console.error("Échec du téléchargement du fichier");
    }
  };

  return (
    <div className="space-y-6 pb-10">
      <ToastContainer position="top-right" theme="colored" limit={3} />

      {/* Header + Controls */}
      <div className="flex flex-col min-sm:flex-row min-sm:items-center min-sm:justify-between gap-5">
        <div>
          <h1 className="text-lg min-sm:text-xl font-bold text-gray-900 dark:text-white tracking-tight">
            Mes commandes
          </h1>
          <p className="mt-1.5 text-gray-500 dark:text-gray-400">
            Gestion et suivi de vos commandes clients
          </p>
        </div>

        <div className="flex justify-end gap-3">

          <Link
            to="/product"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg shadow-sm transition-colors"
          >
            <Plus size={18} />
            Nouvelle commande
          </Link>
        </div>
      </div>

      {/* Main content */}
      {filteredOrders.length === 0 ? (
        <div className="dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-12 text-center">
          <FileText className="h-16 w-16 mx-auto text-gray-400 mb-6" strokeWidth={1} />
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-3">
            Aucune commande trouvée
          </h3>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            {searchTerm || filterStatus !== 'all'
              ? "Aucun résultat ne correspond à vos filtres"
              : "Vous n'avez pas encore de commande en attente de traitement"}
          </p>
        </div>
      ) : (
        <>

          {/* Summary cards */}
          <div className="grid grid-cols-2 min-sm:grid-cols-2 min-lg:grid-cols-4 gap-5">
            {[
              { label: 'Total commandes', value: filteredOrders.length, color: 'text-gray-900 dark:text-white' },
              { label: 'En attente', value: filteredOrders.filter(o => o.etat?.toLowerCase() === 'en attente').length, color: 'text-red-600 dark:text-red-400' },
              { label: 'En cours', value: filteredOrders.filter(o => o.etat?.toLowerCase() === 'en cours').length, color: 'text-amber-600 dark:text-amber-400' },
              { label: 'Terminées', value: filteredOrders.filter(o => o.etat?.toLowerCase() === 'terminée').length, color: 'text-emerald-600 dark:text-emerald-400' }
            ].map((item, i) => (
              <div
                key={i}
                className="dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 shadow-sm"
              >
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{item.label}</p>
                <p className={`text-3xl font-bold ${item.color}`}>
                  {item.value}
                </p>
              </div>
            ))}
          </div>

      {/* Filters & Search */}
      <div className="dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-5">
        <div className="flex flex-col min-sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher par n° de commande..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-emerald-500 focus:border-transparent min-w-[180px]"
          >
            <option value="all">Tous les statuts</option>
            <option value="en attente">En attente</option>
            <option value="en cours">En cours</option>
            <option value="terminée">Terminée</option>
          </select>
        </div>
      </div>
      
          {/* Table */}
          <div className="dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-max">
                <thead className="bg-gray-50 dark:bg-gray-800/60">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">N°</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">N° Commande</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Bon de commande</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">État</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                  {filteredOrders.map((order, idx) => (
                    <tr
                      key={order._id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors group"
                    >
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-200">
                        {idx + 1 + (page - 1) * limit}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                        {order.code_orders || '—'}
                      </td>
                      <td className="px-6 py-4">
                        {order.file && order.file !== '/file/boncommande/' ? (
                          <button
                            onClick={() => handleDownload(order.file, `bon-commande-${order.code_orders}.pdf`)}
                            className="text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors"
                            title="Télécharger le bon de commande"
                          >
                            <Download size={20} />
                          </button>
                        ) : (
                          <Ban size={20} className="text-error" title="Aucun bon de commande" />
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {order.date ? new Date(order.date).toLocaleDateString('fr-TN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        }) : '—'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getStatusStyle(order.etat)}`}>
                          {order.etat || 'Indéfini'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <Link
                          to={`/order-details-client/${order._id}`}
                          className="inline-flex items-center gap-1.5 text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300 font-medium transition-colors"
                        >
                          <Eye size={18} />
                          Détails
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex flex-col min-sm:flex-row min-sm:items-center min-sm:justify-between gap-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Affichage de {(page - 1) * limit + 1} à {Math.min(page * limit, orders.length)} sur {orders.length} résultat(s)
              </div>

              <div className="flex items-center gap-2">
                <select
                  value={limit}
                  onChange={(e) => {
                    const newLimit = Number(e.target.value);
                    setLimit(newLimit);
                    setPage(1);
                  }}
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-700 rounded-md dark:bg-gray-800 text-sm"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="p-2 rounded-md border border-gray-300 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <span className="px-3 py-1.5 text-sm font-medium">
                    {page} / {totalPages || 1}
                  </span>

                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= totalPages}
                    className="p-2 rounded-md border border-gray-300 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OrdersList;